package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material.icons.outlined.ErrorOutline
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import coil3.compose.AsyncImage
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.ui.components.PhotoActionBar
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import org.gymly.util.imagePreviewModel

data class BulkProcessResultItem(
    val title: String,
    val subtitle: String? = null,
    val ok: Boolean,
    /** Id de fila en la planilla (alta masiva). */
    val rowId: String? = null,
    /**
     * Si true, «Corregir planilla» conserva esta fila.
     * Una alta ya creada con cobro fallido se conserva solo para reintentar el cobro
     * (no se vuelve a crear la orden).
     */
    val keepForCorrection: Boolean = !ok && rowId != null,
)

@Composable
fun BulkProcessResultDialog(
    title: String,
    summary: String,
    items: List<BulkProcessResultItem>,
    onDismiss: () -> Unit = {},
    primaryLabel: String = "Continuar",
    secondaryLabel: String? = null,
    onPrimary: () -> Unit = onDismiss,
    onSecondary: (() -> Unit)? = null,
) {
    Dialog(onDismissRequest = onPrimary) {
        Column(
            Modifier
                .widthIn(max = 520.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(20.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(24.dp),
        ) {
            Text(title, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = AdminColors.Text)
            Spacer(Modifier.height(8.dp))
            Text(summary, color = AdminColors.TextMuted, fontSize = 14.sp)
            Spacer(Modifier.height(16.dp))
            val okCount = items.count { it.ok }
            val failCount = items.size - okCount
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                ResultStatChip("$okCount", "Correctos", AdminColors.Primary)
                if (failCount > 0) {
                    ResultStatChip("$failCount", "Con error", AdminColors.Error)
                }
            }
            if (failCount > 0) {
                Spacer(Modifier.height(8.dp))
                val keepCount = items.count { it.keepForCorrection }
                Text(
                    if (keepCount > 0) {
                        "Al corregir solo quedan $keepCount fila(s) con error. " +
                            "Las altas ya creadas solo reintentan su cobro; no se duplican órdenes."
                    } else {
                        "Las altas ya quedaron registradas. Revisa Órdenes/Pagos si algún cobro no se aplicó."
                    },
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
            Spacer(Modifier.height(16.dp))
            Column(
                Modifier
                    .fillMaxWidth()
                    .heightIn(max = 320.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(AdminColors.Surface)
                    .padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                items.forEach { item ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Icon(
                            if (item.ok) Icons.Outlined.CheckCircle else Icons.Outlined.ErrorOutline,
                            contentDescription = null,
                            tint = if (item.ok) AdminColors.Primary else AdminColors.Error,
                            modifier = Modifier.size(22.dp),
                        )
                        Spacer(Modifier.width(10.dp))
                        Column(Modifier.weight(1f)) {
                            Text(item.title, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                            item.subtitle?.let {
                                Text(it, color = AdminColors.TextMuted, fontSize = 12.sp)
                            }
                        }
                    }
                }
            }
            Spacer(Modifier.height(20.dp))
            Button(
                onClick = onPrimary,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                ),
                shape = RoundedCornerShape(12.dp),
            ) {
                Text(primaryLabel, fontWeight = FontWeight.SemiBold)
            }
            if (secondaryLabel != null && onSecondary != null) {
                Spacer(Modifier.height(8.dp))
                TextButton(
                    onClick = onSecondary,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(secondaryLabel, color = AdminColors.TextMuted)
                }
            }
        }
    }
}

@Composable
private fun ResultStatChip(value: String, label: String, color: androidx.compose.ui.graphics.Color) {
    Column(
        Modifier
            .clip(RoundedCornerShape(12.dp))
            .background(color.copy(alpha = 0.1f))
            .padding(horizontal = 16.dp, vertical = 10.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(value, fontWeight = FontWeight.Bold, fontSize = 24.sp, color = color)
        Text(label, fontSize = 12.sp, color = AdminColors.TextMuted)
    }
}

/**
 * Foto grande a la izquierda (primero en el flujo visual) con preview al capturar.
 */
@Composable
fun BulkMemberPhotoBlock(
    firstName: String,
    lastName: String,
    photo: PickedImagePayload?,
    photoUrl: String? = null,
    enabled: Boolean,
    onUpload: () -> Unit,
    onCapture: () -> Unit,
    onClear: () -> Unit,
    modifier: Modifier = Modifier,
    /** Celular: ocupa todo el ancho disponible. Escritorio: 140 dp fijos (sin cambios). */
    fullWidth: Boolean = false,
) {
    val initials = "${firstName.firstOrNull() ?: '?'}${lastName.firstOrNull() ?: '?'}".uppercase()
    val previewModel = when {
        photo != null -> imagePreviewModel(photo.bytes)
        !photoUrl.isNullOrBlank() -> photoUrl
        else -> null
    }
    val hasPhoto = previewModel != null

    Column(
        (if (fullWidth) modifier.fillMaxWidth() else modifier.width(140.dp))
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.Primary.copy(alpha = 0.04f))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(16.dp))
            .padding(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text("Foto", fontWeight = FontWeight.SemiBold, fontSize = 12.sp, color = AdminColors.TextMuted)
        Spacer(Modifier.height(8.dp))
        Box(
            Modifier
                .size(108.dp)
                .clip(CircleShape)
                .background(AdminColors.Outline.copy(alpha = 0.2f))
                .border(2.dp, if (hasPhoto) AdminColors.Primary else AdminColors.Outline, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            if (previewModel != null) {
                key(previewModel.toString().takeLast(40)) {
                    AsyncImage(
                        model = previewModel,
                        contentDescription = "Foto del socio",
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop,
                    )
                }
            } else {
                Text(initials, fontWeight = FontWeight.Bold, fontSize = 32.sp, color = AdminColors.Primary)
            }
        }
        Spacer(Modifier.height(10.dp))
        PhotoActionBar(
            hasPhoto = hasPhoto,
            enabled = enabled,
            onUpload = onUpload,
            onCapture = onCapture,
            onRemove = if (hasPhoto) onClear else null,
            compact = false,
            accent = AdminColors.Primary,
            error = AdminColors.Error,
            uploadTooltip = "Subir foto",
            captureTooltip = "Terminal Hikvision",
        )
    }
}

fun paymentStatusLabel(
    configured: Boolean,
    status: org.gymly.model.AdminMembershipOrderStatus,
    lines: List<PaymentLineDraft>,
    amountCents: Long,
): String {
    if (!configured) return "Sin cobro · pendiente"
    if (status == org.gymly.model.AdminMembershipOrderStatus.COURTESY) return "Cortesía"
    if (lines.isNotEmpty()) {
        return paymentLinesSummaryLabel(lines, (amountCents / 100.0).toString())
    }
    return when (status) {
        org.gymly.model.AdminMembershipOrderStatus.PAID -> "Pagado"
        org.gymly.model.AdminMembershipOrderStatus.PENDING -> "Pendiente"
        else -> status.labelEs()
    }
}
